import { PickOrCreate } from '../primitives/PickOrCreate';
import { PropertyPill } from '../primitives/PropertyPill';
import { UserPicker } from '../primitives/UserPicker';
import {
  formatPropertyPillLabel,
  isPropertyPillMuted,
  resolveScalarDisplay
} from '../primitives/propertyBarDisplay';
import { AttributeRow } from './AttributeRow';
import type { DocumentPropertiesLoadState } from './useDocumentProperties';
import type {
  CustomAttributeDefRow,
  CustomAttributeOptionRow,
  CustomAttributeValueRow
} from '../../lib/vm/useDocumentEditorVM';
import type { Database } from '../../types/database';

type DocumentPropertyDefRow =
  Database['public']['Tables']['document_property_defs']['Row'];

export interface PropertyBarPillRowProps {
  def: DocumentPropertyDefRow;
  value: CustomAttributeValueRow | undefined;
  isDefault: boolean;
  selectOptions: Database['public']['Tables']['document_property_options']['Row'][];
  userOptions: Extract<DocumentPropertiesLoadState, { kind: 'ready' }>['userOptions'];
  editable: boolean;
  testIdPrefix: string;
  commitValue: (propertyDefId: string, value: unknown) => Promise<void>;
  createOption: (
    propertyDefId: string,
    label: string
  ) => Promise<CustomAttributeOptionRow | null>;
}

export function PropertyBarPillRow({
  def,
  value,
  isDefault,
  selectOptions,
  userOptions,
  editable,
  testIdPrefix,
  commitValue,
  createOption
}: PropertyBarPillRowProps) {
  const compatDef = def as unknown as CustomAttributeDefRow;
  const compatOptions = selectOptions as unknown as CustomAttributeOptionRow[];
  const displayValue = resolveScalarDisplay(
    { kind: def.kind, label: def.label },
    value?.value,
    { userOptions, selectOptions }
  );
  const pillLabel = formatPropertyPillLabel(def.label, displayValue);
  const pillMuted = isPropertyPillMuted(displayValue);
  const marker = isDefault ? '✦' : undefined;
  const markerTestId = `${testIdPrefix}-marker-${def.key}`;
  const pillTestId = `${testIdPrefix}-pill-${def.key}`;

  if (def.kind === 'user') {
    const userValue = readBarUserValue(value?.value);
    const opts = (userOptions ?? []).map((u) => ({
      user_id: u.user_id,
      display_name: u.display_name,
      username: u.username
    }));
    return (
      <UserPicker
        id={`document-editor-attr-${def.key}-picker`}
        label={def.label}
        options={opts}
        value={userValue}
        disabled={!editable}
        triggerVariant="pill"
        pillLabel={pillLabel}
        pillMarker={marker}
        pillMarkerTestId={markerTestId}
        pillMuted={pillMuted}
        data-testid={pillTestId}
        onPick={(picked) => {
          const next = picked ? { user_id: picked.user_id } : null;
          void commitValue(def.id, next);
        }}
      />
    );
  }

  if (def.kind === 'single_select') {
    const optValue = readBarOptionValue(value?.value);
    const opts = selectOptions.map((o) => ({ id: o.id, label: o.label }));
    return (
      <PickOrCreate
        id={`document-editor-attr-${def.key}-picker`}
        label={def.label}
        options={opts}
        value={optValue}
        disabled={!editable}
        triggerVariant="pill"
        pillLabel={pillLabel}
        pillMarker={marker}
        pillMarkerTestId={markerTestId}
        pillMuted={pillMuted}
        data-testid={pillTestId}
        onPick={(picked) => {
          const next = picked ? { option_id: picked.id } : null;
          void commitValue(def.id, next);
        }}
        onCreate={async (label) => {
          const row = await createOption(def.id, label);
          if (!row) return null;
          return { id: row.id, label: row.label };
        }}
      />
    );
  }

  return (
    <PropertyPill
      label={def.label}
      displayValue={displayValue}
      marker={marker}
      markerTestId={markerTestId}
      editable={editable}
      testId={pillTestId}
    >
      {() => (
        <AttributeRow
          def={compatDef}
          value={value}
          editable={editable}
          layout="compact"
          autoFocusControl
          userOptions={userOptions}
          selectOptions={compatOptions}
          onCreateOption={createOption}
          onCommit={(_, v) => {
            void commitValue(def.id, v);
          }}
        />
      )}
    </PropertyPill>
  );
}

function readBarUserValue(
  v: CustomAttributeValueRow['value'] | undefined
): { user_id: string } | null {
  if (v == null) return null;
  if (typeof v === 'object' && !Array.isArray(v)) {
    const userId = (v as { user_id?: unknown }).user_id;
    if (typeof userId === 'string' && userId.length > 0) {
      return { user_id: userId };
    }
  }
  return null;
}

function readBarOptionValue(
  v: CustomAttributeValueRow['value'] | undefined
): { option_id: string } | null {
  if (v == null) return null;
  if (typeof v === 'object' && !Array.isArray(v)) {
    const optId = (v as { option_id?: unknown }).option_id;
    if (typeof optId === 'string' && optId.length > 0) {
      return { option_id: optId };
    }
  }
  return null;
}
